import { type Dispatch, type SetStateAction, useState } from 'react';
import { Edge, Node } from 'reactflow';
import type { McpNodeFormState } from '../../../../types/mcpNode';
import { nodeDataAutoSaveDynamic } from '../../../../util/autosave/nodedata_autosave';
import { useDebounceEffect } from '../../../../util/useDebounceEffect';

type SetSaving = (saving: {
  lastSaved?: string;
  status: string;
  finished?: boolean;
  flowId?: string;
}) => void;

type SetSchema = (
  schema: Record<string, { type: string; required: boolean }>,
) => void;

export function useMcpFieldDebounce(
  edges: Edge[],
  id: string,
  flowId: string | undefined,
  setNodeState: Dispatch<SetStateAction<Node[]>>,
  setSaving: SetSaving,
  setSchema: SetSchema,
  markUserEdited: (field: string) => void,
  setFormData: Dispatch<SetStateAction<McpNodeFormState>>,
) {
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: string;
    name: string;
  }>({
    value: '',
    name: '',
  });

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setSaving,
          setNodeState,
          id,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });
        setIsChangingDirectly(false);
        setWhoIsChanging({ value: '', name: '' });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const onChange = (e: { target: { name: string; value: string } }) => {
    markUserEdited(e.target.name);
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setWhoIsChanging({ name: e.target.name, value: e.target.value });
    setIsChangingDirectly(true);
  };

  return { onChange };
}
